import type { DefaultApi } from "@/api";
import { KomboRejectionReason } from "@/models/kombo_rejection_reason";
import { KomboRejectionReasons } from "@/models/kombo_rejection_reasons";
import type { State as RootState } from "@/stores";
import type { Module } from "vuex";

export interface State {
  rejectionReasons: KomboRejectionReasons | null;
  rejectionMessageTemplate: string | null;
  rejectionMessageUpdatedAt: string | null;
}

export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    rejectionReasons: null,
    rejectionMessageTemplate: null,
    rejectionMessageUpdatedAt: null,
  },
  actions: {
    async getRejectionReasons({ commit }) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getRejectionReasons();
      if (response.status === 204) {
        commit("setRejectionReasons", null);
        return null;
      }
      const rejectionReasons = new KomboRejectionReasons(
        response.data.map(
          (r: Partial<KomboRejectionReason>) => new KomboRejectionReason(r),
        ),
      );
      commit("setRejectionReasons", rejectionReasons);
      return rejectionReasons;
    },

    async getRejectionMessage({ commit }) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getRejectionMessage();
      commit("setRejectionMessageTemplate", response.data.message_text);
      commit("setRejectionMessageUpdatedAt", response.data.updated_at ?? null);
      return response.data;
    },

    async putRejectionMessage({ commit }, messageText: string) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.putRejectionMessage({
        message_text: messageText,
      });
      commit("setRejectionMessageTemplate", response.data.message_text);
      commit("setRejectionMessageUpdatedAt", response.data.updated_at ?? null);
      return response.data;
    },

    async postCandidateDecisionReject(
      {},
      {
        positionId,
        candidateId,
        rejectionReasonId,
        rejectionNote,
      }: {
        positionId: number;
        candidateId: number;
        rejectionReasonId?: string | null;
        rejectionNote?: string | null;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postCandidateDecisionReject(
        positionId,
        candidateId,
        {
          rejection_reason_id: rejectionReasonId ?? null,
          rejection_note: rejectionNote ?? null,
        },
      );
      return response.data;
    },

    async postResumeRejection(
      {},
      {
        positionId,
        resumeId,
        rejectionReasonId,
      }: { positionId: number; resumeId: number; rejectionReasonId: string },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postResumeRejection(positionId, resumeId, {
        rejection_reason_id: rejectionReasonId,
      });
      return response.data.status;
    },
  },
  mutations: {
    setRejectionReasons(state, rejectionReasons: KomboRejectionReasons | null) {
      state.rejectionReasons = rejectionReasons;
    },
    setRejectionMessageTemplate(state, template: string) {
      state.rejectionMessageTemplate = template;
    },
    setRejectionMessageUpdatedAt(state, updatedAt: string | null) {
      state.rejectionMessageUpdatedAt = updatedAt;
    },
  },
};
